import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Grid2";
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Collapse,
  Stack,
  useTheme,
  alpha,
  Divider,
} from "@mui/material";
import {
  Science,
  Tune,
  ShowChart,
  PlayArrow,
  Assessment,
  ExpandMore,
  Whatshot,
} from "@mui/icons-material";
import { IForm } from "../../core/form/IForm";
import { Lookups } from "../../config/constants";
import { useConfig } from "../../core/store/selectors/useConfig";
import { useResponsive } from "../../core/hooks/useResponsive";
import { useBTFormConfig } from "./useBTFormConfig";
import BTStatistics from "./components/BTStatistics";
import BTHistory from "./components/BTHistory";
import BTCharts from "./components/BTCharts";
import { ICore } from "../../core/ICore";

const SectionLabel = ({ icon: Icon, title, hint, dense = false }) => {
  const theme = useTheme();
  const size = dense ? 30 : 36;
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
      <Box
        sx={{
          width: size,
          height: size,
          borderRadius: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          bgcolor: alpha(theme.palette.primary.main, 0.12),
          color: theme.palette.primary.main,
        }}
      >
        <Icon sx={{ fontSize: dense ? 18 : 24 }} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="subtitle1" fontWeight={800} sx={{ fontSize: dense ? "0.9rem" : undefined, lineHeight: 1.3 }}>
          {title}
        </Typography>
        {hint && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.3 }}>
            {hint}
          </Typography>
        )}
      </Box>
    </Stack>
  );
};

const BackTesting = () => {
  const theme = useTheme();
  const { getInstrumentsOptions, getHotTokens } = useConfig();
  const { btForm, btData, btRunConfig } = useBTFormConfig();
  const { isPhone, isCompact } = useResponsive();
  const [configOpen, setConfigOpen] = useState(true);
  const primary = theme.palette.primary.main;
  const instrumentOptions = getInstrumentsOptions();

  /**
   * Fold the configuration away once a run produces results, so the output gets the
   * screen rather than the inputs that are now just history. Keyed on `btData` turning
   * truthy rather than on submit, so a failed run leaves the form open for fixing.
   */
  useEffect(() => {
    if (btData) setConfigOpen(false);
  }, [btData]);

  const v = btForm.values;
  const hotTokens = getHotTokens(v.algoKey);

  // Flags symbols on the selected algorithm's hot list with a fire icon in the dropdown.
  const renderSymbolOption = (props, option) => {
    const { key, ...optionProps } = props;
    return (
      <Box component="li" key={key} {...optionProps} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        <span>{option.value}</span>
        {hotTokens.has(String(option.key)) && (
          <Whatshot fontSize="small" titleAccess="Hot token" sx={{ color: "#f57c00", ml: "auto" }} />
        )}
      </Box>
    );
  };

  // Shown on the collapsed header so the settings behind the results stay visible - the settings of
  // the run that produced them (btRunConfig), not the form, which may have been edited since.
  const run = btRunConfig || v;
  const runInstrument = instrumentOptions.find((o) => String(o.key) === String(run.token));
  const configSummary = [
    run.algoKey,
    runInstrument?.value || (run.token ? String(run.token) : "No symbol"),
    `₹${Number(run.tradeBudget || 0).toLocaleString()}`,
    `+${run.profitMargin}% / -${run.lossMargin}%`,
    `${run.forceExit}d max hold`,
    `${run.years}Y history`,
  ];

  const sectionDivider = {
    borderBottom: { xs: `1px solid ${alpha(primary, 0.1)}`, lg: "none" },
    borderRight: { lg: `1px solid ${alpha(primary, 0.1)}` },
  };

  return (
    <Box
      sx={{
        minHeight: "100%",
        bgcolor: alpha(theme.palette.primary.main, 0.02),
        pb: 1.5,
        pt: { xs: 1, md: 1.25 },
        px: { xs: 0.75, sm: 1.5 },
      }}
    >
      <Box sx={{ maxWidth: 1280, mx: "auto" }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
          <Science sx={{ fontSize: isPhone ? 22 : 28, color: primary }} />
          <Typography variant={isPhone ? "h6" : "h5"} fontWeight={800} letterSpacing="-0.02em" noWrap>
            Backtest lab
          </Typography>
        </Stack>

        {/* Inputs — three-column “console” layout, collapsible */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            border: `1px solid ${alpha(primary, 0.14)}`,
            boxShadow: `0 8px 24px ${alpha("#000", 0.04)}`,
            mb: 1.5,
          }}
        >
          <Box
            role="button"
            tabIndex={0}
            aria-expanded={configOpen}
            onClick={() => setConfigOpen((o) => !o)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setConfigOpen((o) => !o);
              }
            }}
            sx={{
              px: 1.5,
              py: 1,
              bgcolor: alpha(primary, 0.06),
              borderBottom: `1px solid ${alpha(primary, 0.1)}`,
              cursor: "pointer",
              userSelect: "none",
              "&:hover": { bgcolor: alpha(primary, 0.1) },
              "&:focus-visible": { outline: `2px solid ${primary}`, outlineOffset: -2 },
            }}
          >
            <Stack direction="row" alignItems="center" gap={1}>
              <Typography variant="overline" fontWeight={800} color="primary" sx={{ lineHeight: 1.2, flex: 1, minWidth: 0 }}>
                Run configuration
              </Typography>
              <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: { xs: "none", sm: "block" } }}>
                {configOpen ? "Hide" : "Edit"}
              </Typography>
              <ExpandMore
                sx={{
                  color: primary,
                  flexShrink: 0,
                  transform: configOpen ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s ease",
                }}
              />
            </Stack>

            {!configOpen && (
              <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.75 }}>
                {configSummary.map((label) => (
                  <Chip
                    key={label}
                    label={label}
                    size="small"
                    variant="outlined"
                    sx={{
                      height: 22,
                      fontSize: "0.68rem",
                      fontWeight: 700,
                      borderColor: alpha(primary, 0.3),
                      color: "text.secondary",
                      "& .MuiChip-label": { px: 0.75 },
                    }}
                  />
                ))}
              </Stack>
            )}
          </Box>

          <Collapse in={configOpen} timeout="auto">
            <IForm form={btForm}>
              <Grid container>
                <Grid size={{ xs: 12, lg: 4 }} sx={{ p: { xs: 1.25, md: 1.5 }, ...sectionDivider }}>
                  <SectionLabel icon={Tune} title="Strategy" hint="Algorithm and symbol" dense={isCompact} />
                  <Stack spacing={1.25}>
                    <ICore.ISelectBox model="algoKey" label="Algorithm" lookup={Lookups.BackTestAlgorithms} />
                    <ICore.IAutoComplete model="token" label="Symbol" options={instrumentOptions} renderOption={renderSymbolOption} />
                  </Stack>
                </Grid>

                <Grid size={{ xs: 12, lg: 4 }} sx={{ p: { xs: 1.25, md: 1.5 }, ...sectionDivider }}>
                  <SectionLabel icon={ShowChart} title="Risk & exits" hint="Budget, targets, holding" dense={isCompact} />
                  <Grid container spacing={1.25}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <ICore.ITextBox label="Trade budget" model="tradeBudget" />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <ICore.ITextBox label="Profit %" model="profitMargin" />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <ICore.ITextBox label="Loss %" model="lossMargin" />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <ICore.ITextBox label="Max holding (days)" model="forceExit" />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid size={{ xs: 12, lg: 4 }} sx={{ p: { xs: 1.25, md: 1.5 }, display: "flex", flexDirection: "column" }}>
                  <SectionLabel icon={Assessment} title="History window" hint="How far back to load" dense={isCompact} />
                  <ICore.ISelectBox model="years" label="Back years" lookup={Lookups.BackYears} />
                  <Box sx={{ flex: 1, minHeight: 4 }} />
                  <Divider sx={{ my: 1 }} />
                  <Button
                    fullWidth
                    size="medium"
                    variant="contained"
                    color="primary"
                    onClick={btForm.handleSubmit}
                    startIcon={<PlayArrow />}
                    sx={{
                      py: 1.25,
                      fontWeight: 800,
                      fontSize: "0.95rem",
                      borderRadius: 2,
                    }}
                  >
                    Run backtest
                  </Button>
                </Grid>
              </Grid>
            </IForm>
          </Collapse>
        </Paper>

        {/* Results */}
        {!btData && (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              textAlign: "center",
              borderRadius: 2,
              border: `1px dashed ${alpha(primary, 0.25)}`,
              bgcolor: alpha(theme.palette.background.paper, 0.8),
            }}
          >
            <Assessment sx={{ fontSize: 36, color: alpha(primary, 0.35), mb: 1 }} />
            <Typography variant="subtitle1" fontWeight={700}>
              No results yet — run a backtest above
            </Typography>
          </Paper>
        )}

        {btData && (
          <Stack spacing={1.5}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 2,
                p: { xs: 1.25, md: 1.5 },
                border: `1px solid ${alpha(primary, 0.12)}`,
                boxShadow: `0 6px 20px ${alpha("#000", 0.03)}`,
              }}
            >
              <Typography variant="overline" fontWeight={800} color="primary" sx={{ display: "block", mb: 0.5 }}>
                Performance summary
              </Typography>
              <Grid container spacing={{ xs: 1.25, md: 1.5 }} alignItems="stretch">
                <Grid size={{ xs: 12, md: 5.5 }}>
                  <BTStatistics tradeStatistics={btData.tradeStates} tradeBudget={btRunConfig?.tradeBudget} />
                </Grid>
                <Grid size={{ xs: 12, md: 6.5 }}>
                  <BTCharts tradeStatistics={btData.tradeStates} />
                </Grid>
              </Grid>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                borderRadius: 2,
                p: { xs: 1.25, md: 1.5 },
                border: `1px solid ${alpha(primary, 0.12)}`,
              }}
            >
              <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1 }}>
                Trade ledger
              </Typography>
              <BTHistory tradeHistory={btData.tradeData} />
            </Paper>
          </Stack>
        )}
      </Box>
    </Box>
  );
};

export default BackTesting;
